/**
 * 素材の取得とメタデータプローブ。
 *
 * 署名付きURL経由の素材 Blob 取得と、画像/音声/動画のメタデータプローブを担う
 * (シナリオ末尾の「ファイルDL」ZIP と動画エディタの XML 書き出しで共用)。
 */
import { fetchMaterialContentBlob } from '../../materials/api';

/**
 * ナレーションスクリプトの冒頭をファイル名用にサニタイズして返す
 */
export function getNarrationPrefix(narration: string): string {
  if (!narration) return '';
  // 冒頭を切り出し（約10文字）、ファイル名に使えない文字を除去
  const truncated = narration.trim().substring(0, 10);
  const sanitized = truncated.replace(/[\\/:*?"<>|\s]/g, '_').replace(/_+/g, '_').replace(/_$/, '');
  return sanitized ? `_${sanitized}` : '';
}


/**
 * 素材IDから高解像度ファイルを取得
 * 個人フォルダ → 共有フォルダの順で試行 (署名付きURL直fetch → API中継)
 */
export async function fetchMaterialBlob(materialId: string): Promise<{ blob: Blob; contentType: string; filename: string } | null> {
  try {
    const blob = await fetchMaterialContentBlob(materialId);
    const contentType = blob.type || 'application/octet-stream';

    // ファイル名を決定
    let extension = 'bin';
    if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
      extension = 'jpg';
    } else if (contentType.includes('image/png')) {
      extension = 'png';
    } else if (contentType.includes('image/webp')) {
      extension = 'webp';
    } else if (contentType.includes('video/mp4')) {
      extension = 'mp4';
    } else if (contentType.includes('video/webm')) {
      extension = 'webm';
    } else if (contentType.includes('audio/mpeg') || contentType.includes('audio/mp3')) {
      extension = 'mp3';
    } else if (contentType.includes('audio/wav')) {
      extension = 'wav';
    } else if (contentType.includes('audio/aac')) {
      extension = 'aac';
    } else if (contentType.includes('audio/ogg')) {
      extension = 'ogg';
    }

    // content-typeが不明(octet-stream等)の場合、マジックバイトで判定
    if (extension === 'bin') {
      const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      if (header[0] === 0xFF && (header[1] & 0xE0) === 0xE0) {
        extension = 'mp3'; // MP3 sync word
      } else if (header[0] === 0x49 && header[1] === 0x44 && header[2] === 0x33) {
        extension = 'mp3'; // ID3 tag
      } else if (header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46) {
        extension = 'wav'; // RIFF header
      }
    }

    return {
      blob,
      contentType,
      filename: `${materialId}.${extension}`,
    };
  } catch (error) {
    console.error(`[Download] Failed to fetch material ${materialId}:`, error);
    return null;
  }
}


/** 画像Blobからピクセルサイズを取得 */
export async function getImageDimensions(blob: Blob): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(blob);
    const { width, height } = bitmap;
    bitmap.close();
    return { width, height };
  } catch {
    return null;
  }
}


/** 音声Blobから実尺(秒)を取得 (metadata のみ読み込み)。BGMのループ配置判定に使う。 */
export async function getAudioDuration(blob: Blob): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const cleanup = () => {
      URL.revokeObjectURL(url);
      audio.src = '';
    };
    audio.onloadedmetadata = () => {
      const d = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : undefined;
      cleanup();
      resolve(d);
    };
    audio.onerror = () => {
      cleanup();
      resolve(undefined);
    };
    audio.src = url;
  });
}


/** 動画Blobからピクセルサイズと実尺を取得 (metadata のみ読み込み) */
export async function getVideoDimensions(blob: Blob): Promise<{ width: number; height: number; durationSeconds?: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.src = '';
    };
    video.onloadedmetadata = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      // duration は Infinity/NaN になることがあるので有限値のみ採用
      const d = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : undefined;
      cleanup();
      if (w && h) resolve({ width: w, height: h, durationSeconds: d });
      else resolve(null);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
    video.src = url;
  });
}
